import { defineComponent as _defineComponent } from 'vue'
import {computed, inject, onMounted, reactive, ref, watch} from "vue";
import CollapsableCard from "@/components/bootstrap/CollapsableCard.vue";
import {NutrientType} from "@/models/Ingredient/NutrientType";
import {LabelConfigurationModel} from "@/models/NutritionLabels/v2/LabelConfigurationModel";
import RecipesRepository from "@/utils/API/RecipesRepository";
import ListRequest, {Filter, Ordering, OrderType} from "@/models/ListRequest";
import {useNotify} from "@/hooks/useNotify";
import {ItemCompact} from "@/models/ItemCompact";
import {LabelConfigurationOption} from "@/models/NutritionLabels/v2/enums/LabelConfigurationOption";
import {debounce} from "@/utils/Helpers";
import {useTranslations} from "@/hooks/useTranslations";
import {
  getVoluntaryNutrients,
  OPTIONAL_CORE_NUTRIENTS,
  SUPPLEMENT_NUTRIENTS,
  OPTIONAL_CORE_SUPPLEMENT_NUTRIENTS
} from "@/models/NutritionLabels/v2/collections/NutrientGroups";
import {Blend} from "@/models/NutritionLabels/v2/Blend";
import {NutritionLabelCountry} from "@/models/NutritionLabels/NutritionLabelCountry";


export default /*#__PURE__*/_defineComponent({
  __name: 'LabelConfiguration',
  props: {
    model: null,
    requestedConfigurationOptions: null,
    country: null,
    shouldExpand: { type: Boolean }
  },
  emits: ["update:model"],
  setup(__props: any, { emit }) {

const props = __props as {
  model: LabelConfigurationModel;
  requestedConfigurationOptions: LabelConfigurationOption[];
  country: NutritionLabelCountry;
  shouldExpand: boolean;
};




const fetchPreview: () => void = inject("bopFetchPreview");

// Local copy of the model
const localModel = reactive({
  ...JSON.parse(JSON.stringify(props.model)),
  recipesToDisplay: [],
  labelWidth: props.model.labelWidth || 7,
  includeSugarAlcohols: props.model.includeSugarAlcohols || false,
  displayNutrientsSideBySide: props.model.displayNutrientsSideBySide || false,
  displayCalories449Text: props.model.displayCalories449Text || false,
  showCalorieFromFatText: props.model.showCalorieFromFatText || false,
  selectedCoreNutrients: props.model.selectedCoreNutrients || [],
  selectedSupplementNutrients: props.model.selectedSupplementNutrients || [],
  selectedVoluntaryNutrients: props.model.selectedVoluntaryNutrients || [],
  includeThcLogo: props.model.includeThcLogo || false,
  includeSaltFootnote: false,
  includeRiColumn: false,
  includePerServingAnalysis: false,
  includeTrafficLightColoring: false,
  includeTextualIndicators: false,
  includeMonoPoly: false,
  includeOmegas: false,
  useCondensedFont: false,
  useParForFrenchServingSize: false,
  useCondensedFormat: false,
  combinedVoluntaryNutrients: false,
  useInlineFootnote: false,
});

const notify = useNotify();
const translations = useTranslations();

const recipeOptions = ref<ItemCompact[]>([]);

const coreNutrientsByCountry = computed(() => {
  switch (props.country) {
  case NutritionLabelCountry.Canada:
    return OPTIONAL_CORE_NUTRIENTS;

  case NutritionLabelCountry.US:
    return OPTIONAL_CORE_SUPPLEMENT_NUTRIENTS;

  default:
    return [];
  }
});

watch(
  () => props.model,
  (newVal) => {
    Object.assign(localModel, newVal);
  },
  { deep: true, immediate: true }
);

watch(
  () => props.country,
  () => {
    localModel.selectedCoreNutrients =
      localModel.selectedCoreNutrients.filter((n) =>
        coreNutrientsByCountry.value.includes(n)
      );

    emitUpdate();
  }
);

watch(
  () => props.requestedConfigurationOptions,
  (newVal) => {
    const areRecipesRequested = newVal.includes(LabelConfigurationOption.RECIPES_AND_UNIT_ANY_QUANTITY)
      || newVal.includes(LabelConfigurationOption.RECIPES_AND_UNIT_TWO);

    if (areRecipesRequested) {
      localModel.recipesToDisplay = [{
        recipeId: null,
        servingSizeGrams: "100"
      }];
    }
  }
);

onMounted(() => {
  fetchRecipeListing();
});

function fetchRecipeListing() {
  const defaultFilters: Filter[] = [{name: "", value: ""}];
  const defaultOrdering: Ordering = {field: "", orderType: OrderType.Asc};

  const reqData = new ListRequest({
    page: 1,
    pageSize: 0,
    filters: defaultFilters,
    orders: [defaultOrdering],
  });

  RecipesRepository.listReducedRecipes(reqData, true)
    .then((response) => {
      recipeOptions.value = response.data.result.map((r) => ({recipeId: r.id, name: r.name, servingSizeGrams: 0})) || [];
      populateRecipesToDisplay();
    })
    .catch((error) => {
      if (error.response) {
        notify.error(
          error.response.data.errorMessages[0].userVisibleMessage
        );
      }
    });
}

function populateRecipesToDisplay() {
  if (props.model.recipesToDisplay && props.model.recipesToDisplay.length > 0) {
    localModel.recipesToDisplay = props.model.recipesToDisplay.map((r) => {
      const matchingOption = recipeOptions.value.find(
        (opt) => opt.id === r.recipeId
      );

      return {
        recipeId: matchingOption ? matchingOption.id : r.recipeId,
        name: matchingOption?.name || "Unknown recipe",
        servingSizeGrams: r.servingSizeGrams?.toString() || "100",
      };
    });
  }

  else {
    localModel.recipesToDisplay = [];
  }
}


function updateRecipe(
  recipeEntry: {recipeId: number, name: string, servingSizeGrams: number},
  selectedItem: {recipeId: number, name: string}
) {
  recipeEntry.recipeId = selectedItem.recipeId;
  emitUpdate();
}

function emitUpdate() {
  emit("update:model", localModel);
  debouncedFetchPreview();
}

function debouncedFetchPreview() {
  debounce(fetchPreview, 1000, "back-of-package-config-label-update");
}

function addBlendRow() {
  localModel.proprietaryBlends.push({unit: "", name: "", value: "", dailyValue: "", subBlends: [] });
  emitUpdate();
}

function removeBlendRow(index: number) {
  localModel.proprietaryBlends.splice(index, 1);
  emitUpdate();
}

function addSubBlendRow(blend: Blend) {
  blend.subBlends.push({
    unit: "",
    name: "",
    value: "",
    dailyValue: ""
  });
  emitUpdate();
}

function removeSubBlendRow(blend: Blend, index: number) {
  blend.subBlends.splice(index, 1);
  emitUpdate();
}

function shouldAllowAddRecipe() {
  const isTwoRecipeOnly = props.requestedConfigurationOptions.includes(LabelConfigurationOption.RECIPES_AND_UNIT_TWO);
  const maxRecipes = isTwoRecipeOnly ? 2 : 6; // temp max value for "arbitrary" mode

  return localModel.recipesToDisplay.length < maxRecipes;
}
function addRecipeRow() {
  const isTwoRecipeOnly = props.requestedConfigurationOptions.includes(LabelConfigurationOption.RECIPES_AND_UNIT_TWO);
  const maxRecipes = isTwoRecipeOnly ? 2 : 6; // temp max value for "arbitrary" mode

  const shouldAddRecipe = shouldAllowAddRecipe();

  if (!shouldAddRecipe) {
    localModel.recipesToDisplay.splice(maxRecipes);
    emitUpdate();
  }

  if (shouldAddRecipe) {
    localModel.recipesToDisplay.push({ recipeId: null, servingSizeGrams: 100 });
    emitUpdate();
  }
}

function removeRecipeRow(index: number) {
  localModel.recipesToDisplay.splice(index, 1);
  emitUpdate();
}

return { __sfc: true,props, emit, fetchPreview, localModel, notify, translations, recipeOptions, coreNutrientsByCountry, fetchRecipeListing, populateRecipesToDisplay, updateRecipe, emitUpdate, debouncedFetchPreview, addBlendRow, removeBlendRow, addSubBlendRow, removeSubBlendRow, shouldAllowAddRecipe, addRecipeRow, removeRecipeRow, CollapsableCard, NutrientType, LabelConfigurationOption, getVoluntaryNutrients, SUPPLEMENT_NUTRIENTS }
}

})